import React from 'react';

interface HeroBadge {
  label: string;
  tone?: 'green' | 'blue' | 'amber' | 'red' | 'cyan' | 'violet';
}

interface HeroStat {
  icon: string;
  label: string;
}

interface PageHeroProps {
  badge?: HeroBadge;
  title: string;
  description: string;
  stats?: HeroStat[];
  actions?: React.ReactNode;
}

const toneClasses: Record<NonNullable<HeroBadge['tone']>, string> = {
  green: 'bg-emerald-300 shadow-[0_0_0_4px_rgba(52,211,153,0.14)]',
  blue: 'bg-blue-300 shadow-[0_0_0_4px_rgba(59,130,246,0.14)]',
  amber: 'bg-amber-300 shadow-[0_0_0_4px_rgba(251,191,36,0.14)]',
  red: 'bg-rose-300 shadow-[0_0_0_4px_rgba(251,113,133,0.14)]',
  cyan: 'bg-cyan-300 shadow-[0_0_0_4px_rgba(34,211,238,0.14)]',
  violet: 'bg-violet-300 shadow-[0_0_0_4px_rgba(167,139,250,0.14)]',
};

const PageHero: React.FC<PageHeroProps> = ({ badge, title, description, stats = [], actions }) => {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
      <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(99,102,241,0.24),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(56,189,248,0.18),transparent_55%)]" />
      <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="space-y-3">
          {badge && (
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className={`h-2 w-2 rounded-full ${toneClasses[badge.tone || 'blue']}`} />
              {badge.label}
            </div>
          )}
          <h1 className="text-3xl font-black tracking-tight sm:text-4xl">{title}</h1>
          <p className="max-w-2xl text-white/70">{description}</p>
          {stats.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              {stats.map((stat) => (
                <span key={`${stat.icon}-${stat.label}`} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <i className={`${stat.icon} text-white/60`}></i>
                  {stat.label}
                </span>
              ))}
            </div>
          )}
        </div>
        {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
      </div>
    </div>
  );
};

export default PageHero;
